import { useEffect, useRef } from 'react';
import { usePostHog } from '@posthog/react';
import { useAuth } from '@/hooks/useAuth';
import { useConsentOptional } from '@/modules/cookieConsent/useConsent';
import { useOrganization } from '@/hooks/useOrganization';
import { registerLureoSuperProps } from '@/modules/analytics/superProperties';
import { toCanonicalDiscoverySource } from '@/components/dialogs/user-info-onboarding-discovery';

/**
 * Manages PostHog persistence mode and identify() calls based on auth + consent state.
 *
 * Rules (per GDPR/ePrivacy brief):
 *  - Logged-out (anonymous):  memory persistence, no identify
 *  - Logged-in, no analytics consent: memory persistence, identify(user.id) on every mount
 *    (memory loses state on reload, so we re-identify each page load)
 *  - Logged-in, analytics consent granted: localStorage+cookie persistence, identify(user.id)
 *
 * When consent is downgraded (analytics revoked):
 *  - posthog.reset() clears the stored identity / super-props
 *  - set_config switches back to memory persistence
 *
 * Must render inside both PostHogProvider and AuthProvider.
 */
export function PostHogConfigurator() {
  const posthog = usePostHog();
  const { user } = useAuth();
  const consentCtx = useConsentOptional();
  const analyticsConsent = consentCtx?.consent?.analytics ?? false;
  const { orgMember } = useOrganization();
  const canonicalDiscoverySource = toCanonicalDiscoverySource(orgMember?.discoverySource);

  // Track previous consent so we can detect downgrade
  const prevAnalyticsConsent = useRef<boolean | null>(null);
  // Track previous user so we can detect logout
  const prevUserId = useRef<string | null>(null);

  useEffect(() => {
    if (!posthog?.__loaded) return;

    const wasConsented = prevAnalyticsConsent.current;
    const isConsented = analyticsConsent;

    // Detect downgrade: had consent, now revoked
    if (wasConsented === true && isConsented === false) {
      // Clear stored identity, super-props, and any PostHog cookies/localStorage
      posthog.reset();
      // reset() drops super properties too; re-register client identity.
      registerLureoSuperProps(posthog);
      posthog.set_config({ persistence: 'memory' });
    } else if (isConsented) {
      posthog.set_config({ persistence: 'localStorage+cookie' });
    } else {
      // No consent (or first run) — ensure we stay in memory mode
      posthog.set_config({ persistence: 'memory' });
    }

    prevAnalyticsConsent.current = isConsented;
  }, [posthog, analyticsConsent]);

  // Reset identity when the user goes or changes, so a second user signing in on the same browser gets a fresh
  // distinct_id and none of the first one's properties. Watching user?.id here catches every sign-out path
  // (useAuth.signOut, MyAccount) and a sign-in straight over another account. Runs before the identify below.
  useEffect(() => {
    if (!posthog?.__loaded) return;
    const currentUserId = user?.id ?? null;
    if (prevUserId.current && prevUserId.current !== currentUserId) {
      posthog.reset();
      // reset() drops super properties too; re-register client identity.
      registerLureoSuperProps(posthog);
    }
    prevUserId.current = currentUserId;
  }, [posthog, user?.id]);

  // Identify (or re-identify) when user, consent, or org member state changes.
  // We call identify on every render where user is available because memory
  // persistence loses the distinct ID on page reload.
  // Only this user's own membership is sent as their properties: right after a switch the org is still loading.
  useEffect(() => {
    if (!posthog?.__loaded || !user?.id) return;
    if (orgMember && orgMember.userId !== user.id) return;
    posthog.identify(user.id, {
      ...(orgMember?.persona != null && { persona: orgMember.persona }),
      ...(orgMember?.commercialIntent != null && { commercial_intent: orgMember.commercialIntent }),
      ...(canonicalDiscoverySource != null && { discovery_source: canonicalDiscoverySource }),
      ...(orgMember?.trackOneMember != null && { track_one_member: orgMember.trackOneMember }),
      ...(orgMember?.trackTwoMember != null && { track_two_member: orgMember.trackTwoMember }),
    });
  }, [posthog, user?.id, analyticsConsent, orgMember, canonicalDiscoverySource]);

  return null;
}
